import type * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router';
import { ArrowRight } from 'lucide-react';
import { Page, Notice } from '@/components/page';
import { CardSkeleton, EmptyState, ErrorState } from '@/components/states';
import { ClicksOverTime } from '@/components/charts';
import { Button } from '@/components/ui/button';
import { Badge, Card, CardBody, CardHeader, CardTitle } from '@/components/ui/primitives';
import { useAdminList } from '@/hooks/use-admin-resource';
import { useAdminRequest } from '@/hooks/use-cursor-page';
import type { components } from '@/api/schema';

type AdminStats = components['schemas']['AdminStatsDto'];
import { ANALYTICS_PAGE_LIMIT, useAnalyticsPage, useDateRange } from '@/hooks/use-analytics';
import {
  RESOURCES,
  type AdminInterest,
  type AdminMarketplace,
  type AdminWishlistCategory,
  type AdminWizardSummary,
  type ClickBucket,
} from '@/api/resources';
import { useRole } from '@/auth/use-session';
import { CONTENT_ROLES, ANALYTICS_ROLES } from '@/auth/rbac';
import { formatNumber } from '@/lib/format';

/**
 * The landing page.
 *
 * ⚠️ Every tile here reads an endpoint that exists. 06 §2 also asks for
 * signups, active users, children created and wishlist items added; none of
 * those has an aggregate endpoint in the contract, and a tile showing a
 * plausible number from nowhere is worse than a tile that is absent. What is
 * missing is named once, at the bottom, rather than mocked.
 *
 * Pending reports and queue health DO have endpoints now, but they belong to
 * roles this page cannot assume: the report queue is `SUPPORT`, queue health is
 * `SUPER_ADMIN`. They are linked to from the sections that own them rather than
 * fetched here behind a role check on a landing page.
 *
 * `ANALYST` never lands here — `landingPathFor` sends them straight to
 * analytics, so no tile on this page has to reason about whether it may draw.
 */
export function DashboardPage(): React.JSX.Element {
  const role = useRole();
  const canSeeContent = role ? CONTENT_ROLES.includes(role) : false;
  const canSeeAnalytics = role ? ANALYTICS_ROLES.includes(role) : false;

  return (
    <Page
      title="Dashboard"
      description="Where things stand today."
      breadcrumbs={[{ label: 'Overview' }]}
      width="wide"
    >
      <div className="space-y-6">
        <StatsPanel />
        {canSeeAnalytics ? <ClicksPanel /> : null}
        {canSeeContent ? <ContentPanel /> : null}
        {!canSeeAnalytics && !canSeeContent ? <SupportPanel /> : null}

        <Notice title="Where the rest of this lives">
          Open reports are on Moderation and queue health on Ops, because each belongs to a
          different role — a counter here would be the only place a role sees a number it cannot
          then act on.
        </Notice>
      </div>
    </Page>
  );
}

/**
 * The counters from `GET /admin/stats` (06 §2).
 *
 * ⚠️ Rendered for EVERY role, with no role check, because the endpoint returns
 * counts and nothing else — no name, no email, no child, nothing to page
 * through. That is what makes it safe for `ANALYST`, which must never reach
 * personal data. If this panel ever needs a list, it needs a role check first.
 */
function StatsPanel(): React.JSX.Element {
  const request = useAdminRequest();
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [error, setError] = useState<unknown>(null);

  const load = useCallback(() => {
    setError(null);
    request<AdminStats>({ url: 'admin/stats', method: 'get' })
      .then(setStats)
      .catch((err: unknown) => setError(err));
  }, [request]);

  useEffect(load, [load]);

  if (error) {
    return (
      <Card>
        <CardBody>
          <ErrorState error={error} onRetry={load} endpoint="GET /admin/stats" />
        </CardBody>
      </Card>
    );
  }

  if (!stats) {
    return (
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {[0, 1, 2, 3].map((i) => (
          <CardSkeleton key={i} />
        ))}
      </div>
    );
  }

  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
      <StatTile
        label="Signups"
        value={formatNumber(stats.signups.last7Days)}
        hint={`last 7 days · ${formatNumber(stats.signups.last30Days)} in 30`}
      />
      {/* ⚠️ "Active" is LAST LOGIN, not `status = ACTIVE` — an active-status
          account that has not opened the app since January is not active in any
          sense this tile means. The caption says so, because the difference is
          invisible otherwise. */}
      <StatTile
        label="Active users"
        value={formatNumber(stats.activeUsers.last7Days)}
        hint={`signed in within 7 days · ${formatNumber(stats.activeUsers.last30Days)} in 30`}
      />
      <StatTile
        label="Children added"
        value={formatNumber(stats.childrenCreated.last7Days)}
        hint={`last 7 days · ${formatNumber(stats.childrenCreated.last30Days)} in 30`}
      />
      <StatTile
        label="Wishlist items"
        value={formatNumber(stats.wishlistItemsAdded.last7Days)}
        hint={`last 7 days · ${formatNumber(stats.wishlistItemsAdded.last30Days)} in 30`}
      />
      <StatTile
        label="Clicks today"
        value={formatNumber(stats.clicksToday)}
        hint="since midnight UTC"
      />
      {/* Linked, not just counted: a number an admin cannot act on from where
          they are reading it is a number they will ask someone else about. */}
      <StatTile
        label="Pending reports"
        value={formatNumber(stats.pendingReports)}
        hint="open or under review"
        to="/moderation"
      />
      <StatTile
        label="Failed jobs"
        value={formatNumber(stats.failedJobs)}
        hint="dead-lettered across all queues"
        to="/ops"
      />
    </div>
  );
}

function ClicksPanel(): React.JSX.Element {
  const [range] = useDateRange(30);
  const { rows, isLoading, error } = useAnalyticsPage<ClickBucket>(
    'admin/analytics/clicks',
    { from: range.from, to: range.to, limit: ANALYTICS_PAGE_LIMIT },
    null,
  );

  const totals = new Map<string, number>();
  for (const row of rows ?? []) totals.set(row.date, (totals.get(row.date) ?? 0) + row.clicks);
  const series = [...totals.entries()]
    .map(([date, clicks]) => ({ date, clicks }))
    .sort((a, b) => a.date.localeCompare(b.date));

  const total = series.reduce((sum, point) => sum + point.clicks, 0);
  const latest = series.at(-1)?.clicks ?? 0;

  return (
    <div className="grid gap-4 lg:grid-cols-3">
      <div className="grid gap-4 lg:col-span-1">
        <StatTile
          label="Clicks, last 30 days"
          value={isLoading ? null : formatNumber(total)}
          hint="From the nightly rollup"
        />
        <StatTile
          label="Most recent day"
          value={isLoading ? null : formatNumber(latest)}
          hint="Today is not counted until the rollup runs"
        />
      </div>

      <Card className="lg:col-span-2">
        <CardHeader className="flex flex-wrap items-center justify-between gap-3">
          <CardTitle>Clicks per day</CardTitle>
          <Button asChild size="sm" variant="ghost">
            <Link to="/analytics/clicks">
              Open analytics
              <ArrowRight aria-hidden="true" />
            </Link>
          </Button>
        </CardHeader>
        <CardBody>
          {isLoading ? (
            <CardSkeleton className="h-[260px] border-0 p-0" />
          ) : error || series.length === 0 ? (
            <EmptyState
              title="No clicks recorded yet"
              description="Once parents start tapping through to products, the trend appears here."
            />
          ) : (
            <ClicksOverTime data={series} />
          )}
        </CardBody>
      </Card>
    </div>
  );
}

function ContentPanel(): React.JSX.Element {
  const interests = useAdminList<AdminInterest>(RESOURCES.interests);
  const categories = useAdminList<AdminWishlistCategory>(RESOURCES.categories);
  const marketplaces = useAdminList<AdminMarketplace>(RESOURCES.marketplaces);
  const wizards = useAdminList<AdminWizardSummary>(RESOURCES.wizards);

  const published = (wizards.rows ?? []).find((version) => version.status === 'PUBLISHED');
  const drafts = (wizards.rows ?? []).filter((version) => version.status === 'DRAFT');

  return (
    <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
      <StatTile
        label="Active interests"
        value={
          interests.isLoading
            ? null
            : formatNumber((interests.rows ?? []).filter((row) => row.isActive).length)
        }
        hint={`${formatNumber(interests.rows?.length ?? 0)} in total`}
        to="/interests"
      />
      <StatTile
        label="Active categories"
        value={
          categories.isLoading
            ? null
            : formatNumber((categories.rows ?? []).filter((row) => row.isActive).length)
        }
        hint={`${formatNumber(categories.rows?.length ?? 0)} in total`}
        to="/categories"
      />
      <StatTile
        label="Affiliate marketplaces"
        value={
          marketplaces.isLoading
            ? null
            : formatNumber((marketplaces.rows ?? []).filter((row) => row.isAffiliate).length)
        }
        hint={`${formatNumber(marketplaces.rows?.length ?? 0)} configured`}
        to="/marketplaces"
      />
      <StatTile
        label="Live wizard"
        value={wizards.isLoading ? null : published ? `v${published.version}` : 'None'}
        hint={
          drafts.length > 0
            ? `${formatNumber(drafts.length)} draft${drafts.length === 1 ? '' : 's'} open`
            : 'No open drafts'
        }
        to="/wizards"
      />
    </div>
  );
}

function SupportPanel(): React.JSX.Element {
  return (
    <Card>
      <CardBody>
        <EmptyState
          title="Nothing waiting for you"
          description="Look up an account from the Users screen when a support case comes in."
          action={
            <Button asChild variant="primary">
              <Link to="/users">
                Go to users
                <ArrowRight aria-hidden="true" />
              </Link>
            </Button>
          }
        />
      </CardBody>
    </Card>
  );
}

/**
 * A stat tile. `value === null` means loading, and the tile keeps its full
 * height while it waits so the grid does not reflow when the numbers land.
 */
function StatTile({
  label,
  value,
  hint,
  to,
}: {
  label: string;
  value: string | null;
  hint?: string;
  to?: string;
}): React.JSX.Element {
  const body = (
    <>
      <p className="text-2xs font-semibold tracking-wider text-ink-faint uppercase">{label}</p>
      <p className="mt-2 h-9 text-3xl leading-9 font-semibold tracking-tight text-ink" data-numeric>
        {value ?? (
          <span className="inline-block h-7 w-20 animate-pulse rounded bg-surface-sunken" />
        )}
      </p>
      {hint ? <p className="mt-1 text-xs text-ink-soft">{hint}</p> : null}
    </>
  );

  if (to) {
    return (
      <Link
        to={to}
        className="rounded-card border border-line bg-surface p-5 transition-colors hover:border-line-strong"
      >
        {body}
      </Link>
    );
  }

  return <div className="rounded-card border border-line bg-surface p-5">{body}</div>;
}

export { Badge };
